import { z } from 'zod';

/**
 * UI translations. Source strings are the English text, so `t('Save')` reads naturally and falls
 * back to English when a translation is missing. Albanian dictionaries live in `src/locales/sq/*.ts`
 * (`pnpm --filter @trafiku/admin i18n:check` lists untranslated strings).
 *
 * The language is fixed for a page load (switching reloads the page), so `t` also works at module
 * level, in toasts and in validation messages.
 */
type Language = 'sq' | 'en';
export const LANGUAGES: { code: Language; label: string }[] = [
  { code: 'sq', label: 'Shqip' },
  { code: 'en', label: 'English' },
];

const STORAGE_KEY = 'trafiku_lang';

function readLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'sq' || stored === 'en') return stored;
  } catch {
    // storage blocked: use the default
  }
  return 'sq';
}

export const language: Language = readLanguage();
/** Locale for dates and numbers. */
export const locale = language === 'sq' ? 'sq-AL' : 'en-GB';

const modules = import.meta.glob<{ default: Record<string, string> }>('../locales/sq/*.ts', { eager: true });
const sq: Record<string, string> = Object.assign({}, ...Object.values(modules).map((m) => m.default));
const dictionary: Record<string, string> = language === 'sq' ? sq : {};

type Vars = Record<string, string | number>;

const interpolate = (text: string, vars?: Vars) =>
  vars ? text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : text;

/** Translates an English UI string; `{name}` placeholders are filled from `vars`. */
export function t(text: string, vars?: Vars): string {
  return interpolate(dictionary[text] ?? text, vars);
}

/** Singular or plural form by count, e.g. `tn(n, '{n} bus', '{n} buses')`. */
export function tn(n: number, one: string, other: string, vars?: Vars): string {
  return t(n === 1 ? one : other, { n, ...vars });
}

export function setLanguage(code: Language) {
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // ignore: the choice then lasts for this page only
  }
  window.location.reload();
}

document.documentElement.lang = language;

/**
 * Messages from the API (English) whose text varies; fixed ones are looked up in the dictionary.
 * Each pattern's English template must also be a dictionary key.
 */
const serverPatterns: [RegExp, string, string[]][] = [
  [/^(.+) not found$/, '{what} not found', ['what']],
  [/^Invalid filter (.+)$/, 'Invalid filter {field}', ['field']],
  [/^Delete the campaign's (\d+) ad\(s\) first$/, "Delete the campaign's {n} ad(s) first", ['n']],
  [
    /^Direction "(.+)" would be left with fewer than two stations$/,
    'Direction "{name}" would be left with fewer than two stations',
    ['name'],
  ],
  [/^Direction is used by line "(.+)"$/, 'Direction is used by line "{name}"', ['name']],
  [/^(\d+) bus\(es\) are assigned to this line$/, '{n} bus(es) are assigned to this line', ['n']],
  [/^At least (\d+) characters$/, 'At least {n} characters', ['n']],
  // Zod's default messages (field errors from the API)
  [/^String must contain at least (\d+) character\(s\)$/, 'At least {n} characters', ['n']],
  [/^String must contain at most (\d+) character\(s\)$/, 'At most {n} characters', ['n']],
  [/^Number must be greater than or equal to (.+)$/, 'Must be at least {n}', ['n']],
  [/^Number must be less than or equal to (.+)$/, 'Must be at most {n}', ['n']],
  [/^Array must contain at least (\d+) element\(s\)$/, 'Choose at least {n}', ['n']],
  [/^Expected .+, received .+$/, 'Invalid value', []],
  [/^Invalid enum value\..*$/, 'Choose one of the options', []],
];

/** Translates an error message from the API, keeping it as is when unknown. */
export function translateServerMessage(message: string): string {
  if (language === 'en') return message;
  if (dictionary[message]) return dictionary[message];
  for (const [re, template, names] of serverPatterns) {
    const m = re.exec(message);
    if (!m) continue;
    const vars: Vars = {};
    names.forEach((name, i) => {
      // Entity names in "… not found" are translated too.
      vars[name] = name === 'what' ? t(m[i + 1]!) : m[i + 1]!;
    });
    return t(template, vars);
  }
  return message;
}

/** Client-side validation messages in the UI language (custom messages in schemas are translated too). */
z.setErrorMap((issue, ctx) => {
  if (issue.message) return { message: translateServerMessage(issue.message) };
  switch (issue.code) {
    case z.ZodIssueCode.invalid_type:
      return {
        message:
          issue.received === 'undefined' || issue.received === 'null' ? t('Required') : t('Invalid value'),
      };
    case z.ZodIssueCode.too_small:
      if (issue.type === 'string')
        return {
          message:
            Number(issue.minimum) <= 1
              ? t('Required')
              : t('At least {n} characters', { n: Number(issue.minimum) }),
        };
      if (issue.type === 'array') return { message: t('Choose at least {n}', { n: Number(issue.minimum) }) };
      return { message: t('Must be at least {n}', { n: Number(issue.minimum) }) };
    case z.ZodIssueCode.too_big:
      if (issue.type === 'string')
        return { message: t('At most {n} characters', { n: Number(issue.maximum) }) };
      return { message: t('Must be at most {n}', { n: Number(issue.maximum) }) };
    case z.ZodIssueCode.invalid_string:
      if (issue.validation === 'email') return { message: t('Invalid e-mail address') };
      if (issue.validation === 'url') return { message: t('Invalid URL') };
      return { message: t('Invalid value') };
    case z.ZodIssueCode.invalid_enum_value:
      return { message: t('Choose one of the options') };
    case z.ZodIssueCode.invalid_date:
      return { message: t('Invalid date') };
    default:
      return { message: translateServerMessage(ctx.defaultError) };
  }
});
